import { containers } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';

import { PerformanceAnalysisDoubleBarChart } from '@data-visualization/performance-analysis-double-bar-chart/PerformanceAnalysisDoubleBarChart';
import StackedChart from '@data-visualization/stacked-chart/StackedChart';
import {
  DefaultSortingOptionsForPerformanceAnalysisboard,
  PerformanceAnalysisboardPg,
  PerformanceAnalysisboardTableId,
} from '@models/PerformanceAnalysisboard';
import { PerformanceAnalysisHeaderIcons } from '@pages/performance-analysis/header-icon/PerformanceAnalysisHeaderIcons';
import {
  PERFORMANCE_ANALYSIS_CARD_TITLE,
  PERFORMANCE_ANALYSIS_LINE_CHART_WARNING_PERIODICITY_MESSAGE,
  PERFORMANCE_ANALYSIS_TREND_CARD_TITLE,
} from '@pages/Utilities';

import { OnExportReadyCallback } from '@ecosystem/export-utils';
import { ExportFormat, useAnalytics } from '@ecosystem/newron-client-lib';

import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { SortFieldDisplayProps } from '@components/design/migrated/Table';
import { useSelector } from 'react-redux';
import { France } from '@const/Countries';
import {
  getSaleTypeFromSortingOption,
  transformRetailerKpiOptionToShareType,
  transformShareTypeToRetailerKpiOption,
} from '@data-visualization/utilities/ChartUtils';
import PerformanceAnalysisLineChartView from '@data-visualization/performance-analysis-line-chart/PerformanceAnalysisLineChartView';
import {
  ExportPerformanceBySegmentResp,
  PerformanceAnalysisParam,
} from '@data-visualization/performance-analysis-line-chart/Types';
import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { RetailerKpiOption, SaleType } from '@models/Products';
import { ProductType } from '@pages/product-analyzer/Product';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { IRootState } from '@state/reducers/Reducers';
import { isLocalCurrencyApplied } from '@utils/currencyUtils/CurrencyUtils';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';
import TableBoardFilter from '@utils/hook/tableFilter/TableBoardFilter';
import { checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries } from '@utils/multiCountryUtils/MultiCountryUtils';
import {
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  TOGGLE_CLICKED_TITLE,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import { TableName } from '@utils/page-analytics/model/TableClickTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { byFilterKey, excludeByFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import {
  CountryFilterPair,
  FactRevenue,
  FeatureOption,
  FilterKeys,
  Filters,
  getPageTypeWithPageName,
  ShareType,
} from '@utils/pageConfig/Types';
import { CollectionButtonProps, CollectionInsightType } from '../collections/Model';
import { PerformanceAnalysisLineChartInsight } from '../collections/performance-analysis-line-chart/Model';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import FeatureSelector from '../feature-selector/FeatureSelector';
import { FeatureSelectorType } from '../feature-selector/Types';
import PerformanceAnalysisboard from '../performance-analysis-board/PerformanceAnalysisboard';
import PerformanceAnalysisCollectionButton from '../performance-analysis-collection-button/PerformanceAnalysisCollectionButton';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { ViewType } from '@hooks/export/Model';
import { PerformanceAnalysisStackedBarChartInsight } from '../collections/performance-analysis-stacked-bar-chart/Model';
import { FilterCriteriaParam } from '@models/CommonParam';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { filterByAccessibleCountries } from '@utils/collectionUtils/CollectionUtils';
import { getGrandTotalValue } from '@utils/TotalUtils';
import { WithAlert } from '@components/design';
import { PerformanceAnalysisView, PerformanceAnalysisViewMapping } from './Types';

interface Props {
  performanceAnalysisboardPg: PerformanceAnalysisboardPg[];
  doubleBarChartData: PerformanceAnalysisboardPg[];
  lineChartData: ExportPerformanceBySegmentResp;
  stackedChartRespData: StackedChartResp[];
  selectedGrowthType: GrowthTypes;
  selectedShareType: ShareType;
  setSelectedShareType: any;
  loadMore: boolean;
  countries: Filters;
  selectedPeriodicity: Periodicity;
  loadingTableAndDoubleBarChart: boolean;
  loadingLineChart: boolean;
  loadingStackedChart: boolean;
  view: ViewType;
  setView: (view: ViewType) => void;
  segmentByFeature: FeatureOption;
  setSegmentByFeature: (f: FeatureOption) => void;
  featureFilters: Filters[];
  setFeature: (feat: FeatureOption) => void;
  isDisabled: boolean;
  feature: FeatureOption;
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  onChartExportReady: OnExportReadyCallback;
  featureHandler: () => void;
  onKpiSelectHandler?: () => void;
  segmentByHandler: (feat: FeatureOption) => void;
  // NEO-24170: Temporary fix for displaying selected currency on certain visualisations
  // REMOVE once all visualisations support multicountry feature
  factRevenueBySelectedCurrency: FactRevenue;
  volume: string;
  isRestoreFromSavedView: boolean;
  setIsRestoreFromSavedView: (isRestoreFromSavedView: boolean) => void;
  isPeriodicityMonthlyOrWeekly: boolean;
  sortByStackedChart: SortFieldDisplayProps;
  setSortByStackedChart: (sortByStackedChart: SortFieldDisplayProps) => void;
  productType: ProductType;
  lineChartCollectionsApiParamProps: PerformanceAnalysisParam;
  stackChartCollectionsApiParamProps: FilterCriteriaParam;
  pgsByAppliedCountries: CountryFilterPair[];
  isApiError?: boolean;
}

const PerformanceAnalysisCard: React.FC<Props> = ({
  performanceAnalysisboardPg,
  doubleBarChartData,
  lineChartData,
  stackedChartRespData,
  selectedGrowthType,
  selectedShareType,
  setSelectedShareType,
  loadMore,
  countries,
  selectedPeriodicity,
  loadingTableAndDoubleBarChart,
  loadingLineChart,
  loadingStackedChart,
  view,
  setView,
  segmentByFeature,
  setSegmentByFeature,
  featureFilters,
  setFeature,
  isDisabled,
  feature,
  onChartDownload,
  onChartExportReady,
  featureHandler,
  onKpiSelectHandler,
  segmentByHandler,
  factRevenueBySelectedCurrency,
  volume,
  isRestoreFromSavedView,
  setIsRestoreFromSavedView,
  isPeriodicityMonthlyOrWeekly,
  sortByStackedChart,
  setSortByStackedChart,
  productType,
  lineChartCollectionsApiParamProps,
  stackChartCollectionsApiParamProps,
  pgsByAppliedCountries,
  isApiError,
}) => {
  const pagesConfig = usePageConfig();
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  //Based on this state StackedChartImage component will render which will be used to export Stacked chart as Image & PPT.
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(false);

  const [updatedFeatureFilters, setUpdatedFeatureFilters] = useState<Filters[]>(featureFilters);

  const [selectedRetailerOption, setSelectedRetailerOption] = useState<RetailerKpiOption>(
    transformShareTypeToRetailerKpiOption(selectedShareType),
  );

  const { sorting } = usePersistedSorting({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });

  const [selectedLineChartSaleType, setSelectedLineChartSaleType] = useState<SaleType>(
    getSaleTypeFromSortingOption(sorting),
  );

  const setSelectedShareTypeFromRetailerKpiOption = (retailerKpiOption: RetailerKpiOption) => {
    setSelectedShareType(transformRetailerKpiOptionToShareType(retailerKpiOption));
  };

  const handleViewChange = (newView: ViewType) => {
    pagesConfig.setIsMultiCountryDropdown(pagesConfig.isMultiCountryFeatureFlagPA);
    setView(newView);
    handleElementClickTracking(newView);
  };

  const handleElementClickTracking = (title: string) => {
    const params: PageElementClickTrackingParam = {
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      chartName: PERFORMANCE_ANALYSIS_CARD_TITLE,
      optionChosen: title,
      elementName: TOGGLE_CLICKED_TITLE,
      access: pagesConfig.access,
    };
    pageElementClickTrackingFunction(params);
  };

  const constructFeatureSelectorType = (): FeatureSelectorType => ({
    isDisabled: isDisabled,
    feature: feature,
    setFeature: setFeature,
    featureFilters: updatedFeatureFilters,
  });

  const renderCardTitle = () => {
    return view === ViewType.LineChart ? PERFORMANCE_ANALYSIS_TREND_CARD_TITLE : PERFORMANCE_ANALYSIS_CARD_TITLE;
  };

  //Start used for Performance Analysis line chart periodicity warning alert
  const isDisplayLineChartWarningMessage = view === ViewType.LineChart && !isPeriodicityMonthlyOrWeekly;

  const [isDismissLineChartWarningMessage, setIsDismissLineChartWarningMessage] = useState<boolean>(false);

  const onDismissLineChartWarningMessage = () => {
    setIsDismissLineChartWarningMessage(true);
  };
  //End used for Performance Analysis line chart periodicity warning alert

  const setFeatureOptions = useCallback((feat: FeatureOption) => setFeature(feat), []);

  const CURRENT_TOTAL_NAME = getGrandTotalValue();

  const noResultHandler = React.useCallback(() => {
    setIsExportButtonDisabled(true);
  }, [performanceAnalysisboardPg, doubleBarChartData, lineChartData, stackedChartRespData]);

  const renderDataView = () => {
    switch (view) {
      case ViewType.LineChart:
        return (
          <PerformanceAnalysisLineChartView
            data={lineChartData}
            selectedPeriodicity={selectedPeriodicity}
            factRevenue={factRevenueBySelectedCurrency}
            loadingLineChart={loadingLineChart}
            selectedShareType={selectedShareType}
            onChangeSelectedShareType={setSelectedShareTypeFromRetailerKpiOption}
            featureSelectorType={constructFeatureSelectorType()}
            featureHandler={featureHandler}
            noResultHandler={noResultHandler}
            volume={volume}
            selectedRetailerOption={selectedRetailerOption}
            setSelectedRetailerOption={setSelectedRetailerOption}
            selectedLineChartSaleType={selectedLineChartSaleType}
            setSelectedLineChartSaleType={setSelectedLineChartSaleType}
            noPeriodExistForMulticountry={
              !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
                pagesConfig.appliedFilters,
                mainFiltersState,
                initialFiltersState,
              )
            }
            trackingTitle={PERFORMANCE_ANALYSIS_CARD_TITLE}
          />
        );
      case ViewType.BarChart:
        return (
          <PerformanceAnalysisDoubleBarChart
            performanceAnalysisboardPg={doubleBarChartData}
            growthType={selectedGrowthType}
            isLoading={loadingTableAndDoubleBarChart}
            featureSelectorType={constructFeatureSelectorType()}
            factRevenue={factRevenueBySelectedCurrency}
            featureHandler={featureHandler}
            onKpiSelectHandler={onKpiSelectHandler}
            noResultHandler={noResultHandler}
            volume={volume}
            noPeriodExistForMulticountry={
              !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
                pagesConfig.appliedFilters,
                mainFiltersState,
                initialFiltersState,
              )
            }
            isApiError={isApiError}
          />
        );
      case ViewType.StackedChart:
        return (
          <StackedChart
            stackedChartRespData={stackedChartRespData}
            showPerformanceWithin={feature}
            isLoading={loadingStackedChart}
            segmentByFeature={segmentByFeature}
            setSegmentByFeature={setSegmentByFeature}
            //By dropdown should remain the same
            featureFilters={
              pagesConfig.isMultiCountryFeatureFlagPA
                ? removePricebandFeatureFromDropdown(featureFilters)
                : featureFilters
            }
            factRevenue={factRevenueBySelectedCurrency}
            featureSelectorType={constructFeatureSelectorType()}
            featureHandler={featureHandler}
            segmentByHandler={segmentByHandler}
            onExportReady={onChartExportReady}
            isExportModalOpen={isExportModalOpen}
            noResultHandler={noResultHandler}
            volume={volume}
            view={view}
            isRestoreFromSavedView={isRestoreFromSavedView}
            setIsRestoreFromSavedView={setIsRestoreFromSavedView}
            sortByStackedChart={sortByStackedChart}
            setSortByStackedChart={setSortByStackedChart}
            noPeriodExistForMulticountry={
              !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
                pagesConfig.appliedFilters,
                mainFiltersState,
                initialFiltersState,
              )
            }
            componentName={`${PERFORMANCE_ANALYSIS_CARD_TITLE} ${
              PerformanceAnalysisViewMapping[PerformanceAnalysisView.StackedBarChart]
            }`}
          />
        );
      default:
        return (
          <>
            <div className="flex-row flex-wrap gap-4 mb-6">
              <div style={{ width: '400px' }}>
                <FeatureSelector
                  key={`${feature}-${loadingTableAndDoubleBarChart}-${updatedFeatureFilters}`} //NOSONAR typescript:S6551
                  isDisabled={isDisabled ?? loadingTableAndDoubleBarChart}
                  label={'Show performance within'}
                  featureValues={updatedFeatureFilters}
                  featureOptions={setFeatureOptions}
                  selectedOption={feature}
                  featureOnChange={featureHandler}
                  chartName={TableName.PERFORMANCE_BY_SEGMENTS}
                />
              </div>
              <div>
                <TableBoardFilter
                  selectedShareType={selectedShareType}
                  onChangeSelectedShareType={setSelectedShareType}
                  chartName={TableName.PERFORMANCE_BY_SEGMENTS}
                />
              </div>
            </div>
            <PerformanceAnalysisboard
              performanceAnalysisboardPg={performanceAnalysisboardPg}
              selectedShareType={selectedShareType}
              selectedGrowthType={selectedGrowthType}
              loadMore={loadMore}
              isLoading={loadingTableAndDoubleBarChart}
              countries={countries}
              factRevenue={factRevenueBySelectedCurrency}
              noResultHandler={noResultHandler}
              volume={volume}
              noPeriodExistForMulticountry={
                !checkIsSelectedTimeFrameAndPeriodExistInAllMultiCountries(
                  pagesConfig.appliedFilters,
                  mainFiltersState,
                  initialFiltersState,
                )
              }
              currentTotalName={CURRENT_TOTAL_NAME}
              isApiError={isApiError}
            />
          </>
        );
    }
  };

  useEffect(() => {
    const isViewLoadingLineChart = view === ViewType.LineChart && loadingLineChart;
    const isViewLoadingStackedChart = view === ViewType.StackedChart && loadingStackedChart;
    const isViewLoadingTableDoubleBarChart =
      (view === ViewType.Table || view === ViewType.BarChart) && loadingTableAndDoubleBarChart;

    if (performanceAnalysisboardPg.length > 0) {
      setIsExportButtonDisabled(
        isViewLoadingLineChart ?? isViewLoadingStackedChart ?? isViewLoadingTableDoubleBarChart,
      );
    }
    removeFeaturesFromDropdown();
  }, [view, loadingLineChart, loadingStackedChart, loadingTableAndDoubleBarChart]);

  useEffect(() => {
    setSelectedLineChartSaleType(getSaleTypeFromSortingOption(sorting));
  }, [sorting]);

  const removePricebandFeatureFromDropdown = (currentFeatureValues: Filters[]): Filters[] => {
    const { pageType, appliedFilters } = pagesConfig;
    const currentlyAppliedFilters = appliedFilters[pageType].selectedFilters;

    const appliedCountry = currentlyAppliedFilters.find(byFilterKey(FilterKeys.COUNTRY));
    const appliedCurrency = currentlyAppliedFilters.find(byFilterKey(FilterKeys.CURRENCY));

    if (
      !appliedCurrency ||
      !appliedCountry ||
      isLocalCurrencyApplied(pagesConfig, productType, appliedCurrency, appliedCountry)
    ) {
      if (appliedCountry && appliedCountry.options.length > 1) {
        return currentFeatureValues.filter(excludeByFilterKey(FilterKeys.PRICEBAND));
      } else {
        return currentFeatureValues;
      }
    }

    return currentFeatureValues.filter(excludeByFilterKey(FilterKeys.PRICEBAND));
  };

  const removeChannelFeatureFromDropdown = (currentFeatureValues: Filters[]): Filters[] => {
    const isStackedChartView = view === ViewType.StackedChart;
    const isFranceTheAppliedCountry = countries.options.some((country) => country.value === France.id);

    return isStackedChartView && isFranceTheAppliedCountry
      ? currentFeatureValues.filter(excludeByFilterKey(FilterKeys.CHANNEL))
      : currentFeatureValues;
  };

  //Remove channel from dropdown for france user
  const removeFeaturesFromDropdown = () => {
    let newFeatureFilters;
    newFeatureFilters = removeChannelFeatureFromDropdown(featureFilters);
    newFeatureFilters = removePricebandFeatureFromDropdown(newFeatureFilters);
    setUpdatedFeatureFilters(newFeatureFilters);
  };

  const { Card } = containers;

  const isViewSupportCollections = (currentView: ViewType) =>
    [ViewType.LineChart, ViewType.StackedChart].includes(currentView);

  const createLineChartInsightDataFn: CreateInsightDataFn<PerformanceAnalysisLineChartInsight> = () => {
    const {
      periodType,
      productGroup = [],
      countries: collectionCountries,
      appliedFilters,
    } = lineChartCollectionsApiParamProps;

    const filteredApiParams = filterByAccessibleCountries(
      appliedFilters,
      pgsByAppliedCountries,
      periodType,
      collectionCountries,
      productGroup,
    );
    return {
      apiParams: {
        ...lineChartCollectionsApiParamProps,
        ...filteredApiParams,
      },
      insightCardTitle: PERFORMANCE_ANALYSIS_CARD_TITLE,
      selectedLineChartSaleType: selectedLineChartSaleType,
      selectedRetailerKpiOption: selectedRetailerOption,
      selectedFeature: feature,
      currencySymbol: factRevenueBySelectedCurrency.currencySymbol,
      retailerClientName: retailerClientState.clientName,
    };
  };

  const createStackedChartInsightDataFn: CreateInsightDataFn<PerformanceAnalysisStackedBarChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const {
      periodType,
      productGroup = [],
      countries: collectionCountries,
      appliedFilters,
    } = stackChartCollectionsApiParamProps;

    const filteredApiParams = filterByAccessibleCountries(
      appliedFilters,
      pgsByAppliedCountries,
      periodType,
      collectionCountries,
      productGroup,
    );
    return {
      apiParams: {
        ...stackChartCollectionsApiParamProps,
        ...filteredApiParams,
      },
      insightCardTitle: PERFORMANCE_ANALYSIS_CARD_TITLE,
      segmentByFeature: segmentByFeature,
      showPerformanceWithin: feature,
      sortByStackedChart: sortByStackedChart,
      currencySymbol: factRevenueBySelectedCurrency.currencySymbol,
      selectedGrowthType: selectedGrowthType,
      volume: volume,
      collectionDataType: collectionDataType,
      retailerClientName: retailerClientState.clientName,
    };
  };

  /**
   * getChartInsightDataFnBasedOnChartType to cater to different chart types
   * Eg: Line Chart/Stacked Bar chart
   */
  const getChartInsightDataFnBasedOnChartType = (currentView: ViewType) => {
    if (currentView === ViewType.StackedChart) {
      return createStackedChartInsightDataFn;
    }
    return createLineChartInsightDataFn;
  };
  const getCollectionButtonProps = (currentView: ViewType): CollectionButtonProps => {
    if (currentView === ViewType.LineChart)
      return {
        type: CollectionInsightType.PerformanceAnalysisLineChart,
      };
    return {
      type: CollectionInsightType.PerformanceAnalysisStackedBarChart,
    };
  };
  return (
    <Card>
      <Card.Title title={renderCardTitle()}>
        <Card.Title.Actions>
          {isViewSupportCollections(view) && (
            <div
              className={
                !isExportButtonDisabled
                  ? 'flex items-center gap-4 opacity-100 mx-2'
                  : 'flex items-center gap-4 opacity-25 mx-2 pointer-events-none'
              }
            >
              <PerformanceAnalysisCollectionButton
                collectionButtonProps={getCollectionButtonProps(view)}
                createInsightDataFn={getChartInsightDataFnBasedOnChartType(view)}
                key={view}
                enableDataType={view === ViewType.StackedChart}
              />
            </div>
          )}
          <PerformanceAnalysisHeaderIcons
            onChartDownload={onChartDownload}
            view={view}
            setIsExportModalOpen={setIsExportModalOpen}
            isExportButtonDisabled={isExportButtonDisabled}
            volume={volume}
            onChange={handleViewChange}
          />
        </Card.Title.Actions>
      </Card.Title>
      {!isDismissLineChartWarningMessage && isDisplayLineChartWarningMessage && (
        <div className="my-4" data-testid="periodicity-alert-warning-message">
          <WithAlert
            title={PERFORMANCE_ANALYSIS_LINE_CHART_WARNING_PERIODICITY_MESSAGE}
            onClick={onDismissLineChartWarningMessage}
          />
        </div>
      )}
      {renderDataView()}
    </Card>
  );
};

export default PerformanceAnalysisCard;
